import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import useApi from "../../hooks/useApi";
import useGeneros from "../../hooks/useGeneros";
import countries from "../../utils/countries";

export default function DirectLinkSolicitud() {
  const { slug, serviceId } = useParams();
  const navigate = useNavigate();
  const { loadApi, loadingApi } = useApi();
  const generos = useGeneros();
  const countryOptions = Object.entries(countries).map(([value, label]) => ({
    value,
    label,
  }));
  const [difusor, setDifusor] = useState(null);
  const [service, setService] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({});
  const [cover, setCover] = useState(null);
  const [presskit, setPresskit] = useState(null);

  useEffect(() => {
    loadApi(`general/direct-links/${encodeURIComponent(slug)}`, false, "get")
      .then(({ data }) => {
        const selected = data.services?.find((item) => item.id === serviceId);
        if (!selected) {
          setError("Este servicio no está disponible en el Direct Link.");
          return;
        }
        setDifusor(data);
        setService(selected);
      })
      .catch((e) => setError(e.message));
  }, [slug, serviceId]);

  const change = (name) => (e) =>
    setForm((prev) => ({ ...prev, [name]: e.target.value }));

  const enviar = async () => {
    setError("");
    try {
      const body = {
        ...form,
        serviceId,
        cover,
        ...(presskit ? { presskit } : {}),
      };
      const { data } = await loadApi(
        `general/direct-links/${encodeURIComponent(slug)}/intents`,
        false,
        "post",
        body,
        true
      );
      sessionStorage.setItem("directLinkIntentToken", data.token);
      navigate(
        `/direct-link/resumen?intent=${encodeURIComponent(data.token)}`
      );
    } catch (e) {
      setError(e.message);
    }
  };

  if (!difusor && !error) {
    return (
      <Stack minHeight="100vh" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Stack>
    );
  }

  if (error && !service) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="warning">{error}</Alert>
      </Container>
    );
  }

  const esEvento = service?.tipoContenido === "evento";

  return (
    <Box minHeight="100vh" bgcolor="#f4f4f6" py={{ xs: 3, md: 6 }}>
      <Container maxWidth="md">
        <Card sx={{ borderRadius: 4 }}>
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Stack direction="row" alignItems="center" spacing={2} mb={3}>
              <Avatar src={difusor?.profileimg || undefined} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Solicitud directa para
                </Typography>
                <Typography variant="h6" fontWeight={900}>
                  {difusor?.nombre_media}
                </Typography>
              </Box>
            </Stack>

            <Typography variant="h4" fontWeight={900}>
              {service?.nombre}
            </Typography>
            <Typography color="text.secondary" mt={1}>
              {service?.descripcion}
            </Typography>
            <Typography color="primary.main" fontWeight={900} mt={1}>
              5 RadarCoins · USD $12.50
            </Typography>

            <Alert severity="info" sx={{ mt: 3 }}>
              No necesitas una cuenta para empezar. Completa la solicitud, realiza
              el pago y al final activaremos tu cuenta gratuita de RadarFreim para
              que puedas darle seguimiento.
            </Alert>

            {error && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {error}
              </Alert>
            )}

            <Stack spacing={2.5} mt={4}>
              <Typography variant="h6" fontWeight={900}>
                Datos del proyecto
              </Typography>
              <TextField
                label="Nombre del proyecto artístico"
                placeholder="Ej. Los Tomacitos"
                value={form.projectName || ""}
                onChange={change("projectName")}
                required
                fullWidth
              />
              <TextField
                label="Nombre del responsable"
                placeholder="La persona con quien dará seguimiento el medio"
                value={form.responsibleName || ""}
                onChange={change("responsibleName")}
                required
                fullWidth
              />
              <TextField
                label="Correo electrónico"
                type="email"
                value={form.email || ""}
                onChange={change("email")}
                required
                fullWidth
              />
              <Autocomplete
                options={countryOptions}
                value={
                  countryOptions.find(
                    (item) => item.value === form.projectCountry
                  ) || null
                }
                onChange={(e, value) =>
                  setForm((prev) => ({
                    ...prev,
                    projectCountry: value?.value || "",
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="País del proyecto"
                    required
                  />
                )}
              />
              <Autocomplete
                options={generos}
                value={form.genre || null}
                onChange={(e, value) =>
                  setForm((prev) => ({
                    ...prev,
                    genre: value || "",
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Género musical principal"
                    required
                  />
                )}
              />
              <TextField
                label="Biografía breve del proyecto"
                helperText="De dónde son, integrantes, trayectoria e información que ayude al medio. Si tienes press kit, también puedes adjuntarlo."
                value={form.projectBio || ""}
                onChange={change("projectBio")}
                multiline
                minRows={4}
                fullWidth
              />

              {!esEvento ? (
                <>
                  <Typography variant="h6" fontWeight={900} pt={2}>
                    Tu lanzamiento
                  </Typography>
                  <TextField
                    label="Nombre de la canción o lanzamiento"
                    value={form.releaseName || ""}
                    onChange={change("releaseName")}
                    required
                    fullWidth
                  />
                  <TextField
                    label="Link de Spotify, YouTube, SoundCloud o Bandcamp"
                    value={form.url || ""}
                    onChange={change("url")}
                    required
                    fullWidth
                  />
                  <TextField
                    label="Cuéntanos sobre este lanzamiento"
                    helperText="Historia, fecha de lanzamiento, concepto y cualquier dato útil para el medio."
                    value={form.description || ""}
                    onChange={change("description")}
                    multiline
                    minRows={4}
                    required
                    fullWidth
                  />
                  <Button component="label" variant="outlined">
                    {cover ? cover.name : "Subir portada del lanzamiento *"}
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(e) => setCover(e.target.files?.[0] || null)}
                    />
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="h6" fontWeight={900} pt={2}>
                    Tu evento
                  </Typography>
                  <TextField
                    label="Nombre del evento"
                    value={form.eventName || ""}
                    onChange={change("eventName")}
                    required
                    fullWidth
                  />
                  <TextField
                    label="Fecha del evento"
                    type="date"
                    value={form.eventDate || ""}
                    onChange={change("eventDate")}
                    InputLabelProps={{ shrink: true }}
                    required
                    fullWidth
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label="Ciudad"
                      value={form.city || ""}
                      onChange={change("city")}
                      required
                      fullWidth
                    />
                    <TextField
                      label="País"
                      value={form.country || ""}
                      onChange={change("country")}
                      required
                      fullWidth
                    />
                  </Stack>
                  <TextField
                    label="Venue o lugar"
                    value={form.venue || ""}
                    onChange={change("venue")}
                    fullWidth
                  />
                  <TextField
                    label="Descripción del evento"
                    value={form.description || ""}
                    onChange={change("description")}
                    multiline
                    minRows={4}
                    required
                    fullWidth
                  />
                  <TextField
                    label="Link de boletos"
                    value={form.ticketUrl || ""}
                    onChange={change("ticketUrl")}
                    fullWidth
                  />
                  <Button component="label" variant="outlined">
                    {cover ? cover.name : "Subir flyer del evento *"}
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(e) => setCover(e.target.files?.[0] || null)}
                    />
                  </Button>
                </>
              )}

              <Button component="label" variant="outlined">
                {presskit ? presskit.name : "Adjuntar press kit PDF (opcional)"}
                <input
                  hidden
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => setPresskit(e.target.files?.[0] || null)}
                />
              </Button>

              <LoadingButton
                size="large"
                loading={loadingApi.some((item) =>
                  item.startsWith("post__general/direct-links/")
                )}
                onClick={enviar}
              >
                Revisar solicitud
              </LoadingButton>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
